'use client';

import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Modal } from '../Modal';
import { Breadcrumbs } from '@/components/ui-components/Breadcrumbs';
import Dropdown from '@/components/forms/controls/Dropdown';
import Button from '@/components/ui-components/Button';
import { AlertFilters } from '@/types/alert';
import { i18n } from '@/config/i18n';
import { DropdownOption } from '@/types/components';

const FilterModalContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding: 24px;
`;

const FilterSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const FilterActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding-top: 16px;
  border-top: 1px solid #e5e7eb;
  margin-top: 8px;
`;

const ModalHeaderStyled = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 24px;
  border-bottom: 1px solid #e5e7eb;
  background: #ffffff;
`;

const CloseButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: none;
  color: #6b7280;
  cursor: pointer;
  padding: 8px;
  width: 36px;
  height: 36px;
  line-height: 0;
  transition: all 0.2s ease;
  border-radius: 6px;
  flex-shrink: 0;
  
  svg {
    width: 20px;
    height: 20px;
  }
  
  &:hover {
    background: #f3f4f6;
    color: #111827;
  }
`;

const CloseIcon = () => (
  <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
  </svg>
);

export interface AlertsFilterModalProps {
  isOpen: boolean;
  onClose: () => void;
  onApply: (filters: AlertFilters) => void;
  activeFilters?: AlertFilters;
}

const originOptions: DropdownOption[] = [
  { label: i18n.triage.filters.options.allOrigins, value: 'all' },
  { label: i18n.triage.filters.options.monitor, value: 'MONITOR' },
  { label: i18n.triage.filters.options.scraper, value: 'SCRAPER' },
  { label: i18n.triage.filters.options.logAnalyzer, value: 'LOG_ANALYZER' },
];

const severityOptions: DropdownOption[] = [
  { label: i18n.triage.filters.options.allSeverities, value: 'all' },
  { label: i18n.triage.filters.options.critical, value: 'critical' },
  { label: i18n.triage.filters.options.high, value: 'high' },
  { label: i18n.triage.filters.options.medium, value: 'medium' },
  { label: i18n.triage.filters.options.low, value: 'low' },
];

export const AlertsFilterModal: React.FC<AlertsFilterModalProps> = ({
  isOpen,
  onClose,
  onApply,
  activeFilters = {},
}) => {
  const [filters, setFilters] = useState<AlertFilters>({
    origin: activeFilters.origin || 'all',
    severity: activeFilters.severity || 'all',
  });

  // Update filters when activeFilters change
  useEffect(() => {
    if (isOpen) {
      setFilters({
        origin: activeFilters.origin || 'all',
        severity: activeFilters.severity || 'all',
      });
    }
  }, [activeFilters, isOpen]);

  const handleReset = () => {
    setFilters({
      origin: 'all',
      severity: 'all',
    });
  };

  const handleApply = () => {
    const appliedFilters: AlertFilters = {
      ...(filters.origin && filters.origin !== 'all' && { origin: filters.origin }),
      ...(filters.severity && filters.severity !== 'all' && { severity: filters.severity as 'critical' | 'high' | 'medium' | 'low' }),
    };
    onApply(appliedFilters);
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} maxWidth="600px">
      <ModalHeaderStyled>
        <Breadcrumbs
          items={[{ label: i18n.triage.filters.title }]}
          accentColor="#FB6F2A"
        />
        <CloseButton onClick={onClose}>
          <CloseIcon />
        </CloseButton>
      </ModalHeaderStyled>
      <FilterModalContent>
        <FilterSection>
          <Dropdown
            label={i18n.triage.filters.alerts.origin}
            options={originOptions}
            value={filters.origin || 'all'}
            onChange={(value) => setFilters({ ...filters, origin: value })}
            variant="form"
            useFixedPosition={true}
          />
        </FilterSection>

        <FilterSection>
          <Dropdown
            label={i18n.triage.filters.alerts.severity}
            options={severityOptions}
            value={filters.severity || 'all'}
            onChange={(value) => setFilters({ ...filters, severity: value })}
            variant="form"
            useFixedPosition={true}
          />
        </FilterSection>

        <FilterActions>
          <Button variant="outline" onClick={handleReset}>
            {i18n.triage.filters.alerts.resetFilters}
          </Button>
          <Button variant="primary" onClick={handleApply}>
            {i18n.triage.filters.alerts.applyFilters}
          </Button>
        </FilterActions>
      </FilterModalContent>
    </Modal>
  );
};

export default AlertsFilterModal;

